import { useState, useMemo, useEffect, useRef } from "react";
import { ArrowUpRight, ChevronDown, Handshake, LayoutGrid, Quote } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { HERO_IMAGES } from "@/data/products";
import { DemoBanner } from "@/components/DemoBanner";
import { SEO } from "@/components/SEO";
import { WellsideDealerFleetModal } from "@/components/WellsideDealerFleetModal";
import { HoverImage } from "@/components/HoverImage";
import { trackMetaEvent, trackEvent } from "@/components/Analytics";
import { HeroTrustBadgesV2 } from "@/components/HeroTrustBadgesV2";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => n?.toLocaleString("en-NZ");

// Genuine Toyota wellside tub (not a Bushtech canopy) — cross-sold right after the 79 Series canopy,
// since a customer needs an actual wellside tub before a canopy can be fitted to it.
// Static fallback only (used while the live bullbar fetch is loading) — price/priceOnRequest
// come from the live product record below so this never goes stale vs the real Bull Bars listing.
const WELLSIDE_TUB_FALLBACK = {
  slug: "original-toyota-wellside-79",
  name: "Original Toyota Wellside for 79 Series",
  price: 7990,
  priceOnRequest: false,
  tagline: "Genuine Original · Not Aftermarket",
  thumb: "/products/bullbars/731077_0a04d6b640ec4d889100b00ba7f6f127~mv2.jpg",
};

const buildStoreItems = (models, wellsideTub) => {
  const items = [...models];
  const idx = items.findIndex((m) => m.slug === "land-cruiser-79-series");
  if (wellsideTub && idx !== -1) {
    items.splice(idx + 1, 0, {
      slug: wellsideTub.slug,
      name: wellsideTub.name,
      variants: wellsideTub.tagline,
      price: wellsideTub.price,
      priceOnRequest: wellsideTub.priceOnRequest,
      badge: "Genuine OEM",
      thumb: wellsideTub.thumb,
      externalLink: `/bull-bars/${wellsideTub.slug}`,
    });
  }
  return items;
};

const WellsideCard = ({ m, index }) => (
  <Link
    to={m.externalLink || `/wellside-canopies/${m.slug}`}
    data-testid={`wellside-card-${m.slug}`}
    onClick={() => trackMetaEvent("WellsideProductClick", {
      content_ids: [m.slug],
      content_name: m.name,
      content_type: "product",
      currency: "NZD",
      value: m.priceOnRequest ? undefined : m.price,
    }, undefined, true)}
    className="group relative block overflow-hidden border border-[var(--border-default)] hover:border-[var(--brand-primary)] transition-colors bg-[var(--bg-secondary)]"
  >
    <div className="aspect-[4/3] bg-[var(--bg-tertiary)] overflow-hidden relative">
      <HoverImage primary={m.thumb} secondary={m.gallery?.[1]?.src} alt={m.name} />
      <div className="absolute top-4 left-4">
        {m.badge && (
          <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-black bg-[var(--brand-primary)] px-2.5 py-1">
            {m.badge}
          </span>
        )}
      </div>
    </div>
    <div className="p-5 flex flex-col gap-3">
      <div>
        <h3 className="font-display text-xl tracking-wide text-white leading-tight">{m.name}</h3>
        <p className="text-xs text-[var(--text-tertiary)] mt-1 font-mono uppercase tracking-wider">{m.variants}</p>
      </div>
      <div className="flex items-end justify-between pt-3 border-t border-[var(--border-default)]">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--text-tertiary)]">{m.priceOnRequest ? "" : "From"}</p>
          <p className="font-display text-2xl text-[var(--brand-primary)] leading-none mt-1">
            {m.priceOnRequest ? "Enquire" : `$${fmt(m.price)}`}
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.15em] text-white/70 group-hover:text-[var(--brand-primary)] transition-colors">
          View <ArrowUpRight size={14} />
        </span>
      </div>
    </div>
  </Link>
);

const PROOF_POINTS = [
  {
    image: "/products/bushtech-proof/tailgate-seal-lifestyle.webp",
    objectPosition: "65% 35%",
    label: "01 — Rear Door Fit",
    title: "MOULDED TO YOUR TAILGATE. NOT JUST SITTING ON IT.",
    body: "On most canopies, the rear door just sits straight over the top of the tailgate — a rough approximation that leaves gaps for dust and water to work their way in. Every Bushtech rear door is precision-designed to the exact tailgate shape of each make, model and year ute it's built for. That purpose-built fit is a huge part of why Bushtech canopies are the best-sealed, most dust-proof canopies on the market. It's a security benefit too.",
  },
  {
    image: "/products/bushtech-proof/jetwash-seal.webp",
    imageStyle: { transform: "scale(1.14)", transformOrigin: "50% 0%" },
    label: "02 — Seal Integrity",
    title: "NO SILICONE. NO LEAKS. EVEN AT THE JET WASH.",
    body: "A 20mm suspension-rubber base and a continuous waterproof hinge — rated to 50,000 open/close cycles — do all the sealing work. Ever seen bolt-together canopies covered in silicone in an attempt to get them watertight? There's no silicone needed under that beefy suspension base rubber along the sides on a Bushtech canopy. Take it to the jet wash and not a drop gets inside. Bushtech canopies are engineered to a dust-sealing standard that goes far beyond simply being waterproof.",
  },
  {
    image: "/products/bushtech-proof/dust-test-clean-interior.webp",
    objectPosition: "50% 35%",
    label: "03 — Dust Sealing",
    title: "400KM OF GRAVEL & OFF-ROAD DUST. STAYS THIS CLEAN.",
    body: "Many canopies in NZ fill with dust within a few kilometres on a gravel road. Bushtech canopies offer a totally different experience — built to stay clean for days on end in the African deserts, a far tougher environment. They're specifically engineered to resist the vibration-induced dust ingress that occurs when corrugations and potholes cause rubber seals to let dust slip past. Here in New Zealand, our test Hilux completed a 400km gravel route with billowing dust the whole way — down the Molesworth and back up the Rainbow — and came home as clean as in this photo. For optimal results, the optional Bushtech dust sealing kit should be fitted to your ute wellside/tub.",
  },
  {
    image: "/products/bushtech-proof/load-strength.jpg",
    objectPosition: "50% 40%",
    label: "04 — Load Capacity",
    title: "300KG DYNAMIC. TESTED TO 2.5 TONNES. STRONGEST IN NZ BY FAR.",
    lead: "Why built so much stronger than all others?",
    body: "For reference, other metal canopies in NZ have dynamic load capacities between 100kg and 200kg. None are close to Bushtech's outright strength or strength to weight ratio — and it's worth checking whether a competitor's rating applies to on-road use only. Bushtech started as canopies for ute fleets running predominantly off-road in the African deserts, hauling serious roof loads — a totally different construction standard was required. A hard-shell roof tent, roof rack and awning setup can easily weigh 140kg+; that's already pushing the limit for most metal canopies on-road — possibly too much for them long term off-road. But on a Bushtech canopy it's under 50% of capacity, and on any terrain. Bushtech canopies were built specifically with 4WDing in mind, not just tradie ladders —",
    emphasis: "and despite their class-leading strength, they weigh less than even plastic or fibreglass equivalents.",
  },
  {
    image: "/products/bushtech-proof/gullwing-doors.webp",
    objectPosition: "40% 45%",
    label: "05 — Gullwing Side Access",
    title: "SIDE ACCESS THAT CHANGES HOW YOU USE YOUR UTE.",
    body: "Bushtech's market-leading frame strength also delivers some of the biggest side door openings on the market. The door rubber seals cleverly mount on the doors themselves, not the frame, keeping them out of harm's way when the doors swing wide open. On other canopies, seals mounted on the frame can tear when you try to take advantage of that same side access. Side access this generously sized is transformational to how you actually use your ute. Bolt-together metal, plastic and fibreglass canopies simply don't come close.",
  },
  {
    image: "/products/bushtech-proof/powdercoat-finish-hilux.webp",
    objectPosition: "60% 40%",
    label: "06 — Finish Quality",
    title: "NO SHORTCUTS UNDER THE POWDER-COAT.",
    body: "Bushtech never powder-coats over weld scars — every frame is hand-finished first for a seamless appearance. And it's not your usual powder-coat either — this premium-grade finish continues looking new year after year, even under full-time NZ sun. Built with pride, and backed by a 10-year manufacturer frame guarantee.",
  },
];

// Real customer quotes — only initials used for attribution, not the full
// name either customer posted publicly under.
const TESTIMONIALS = [
  {
    quote: "These are great canopies. The fit is good and are very solid. Best upgrade I've done to my ute so far!",
    attribution: "M.E.",
  },
  {
    quote: "The only canopy I've had that's been 100% watertight.",
    attribution: "F.M.",
  },
];

const MAKE_ORDER = ["Toyota", "Ford", "Volkswagen", "Isuzu", "Mazda", "Nissan", "Jeep", "JAC", "Holden", "INEOS", "Land Rover", "BYD", "GWM"];

const deriveMake = (name) => {
  if (name.includes("Toyota")) return "Toyota";
  if (name.includes("Ford")) return "Ford";
  if (name.includes("VW") || name.includes("Amarok")) return "Volkswagen";
  if (name.includes("Isuzu")) return "Isuzu";
  if (name.includes("Mazda")) return "Mazda";
  if (name.includes("Nissan")) return "Nissan";
  if (name.includes("Jeep")) return "Jeep";
  if (name.includes("JAC")) return "JAC";
  if (name.includes("Holden")) return "Holden";
  if (name.includes("INEOS")) return "INEOS";
  if (name.includes("Land Rover")) return "Land Rover";
  if (name.includes("BYD")) return "BYD";
  if (name.includes("GWM")) return "GWM";
  return "Other";
};

export default function WellsideCanopiesPage() {
  const [selectedMake, setSelectedMake] = useState("all");
  const [models, setModels] = useState(null); // null = loading
  const [wellsideTub, setWellsideTub] = useState(WELLSIDE_TUB_FALLBACK);
  const [expandedProof, setExpandedProof] = useState({});
  const [dealerModalOpen, setDealerModalOpen] = useState(false);
  const [showModelsCTA, setShowModelsCTA] = useState(false);
  const rangeBannerRef = useRef(null);
  const rangeBannerSeenRef = useRef(false);

  useEffect(() => {
    axios.get(`${API}/api/products/wellsides`).then((res) => setModels(res.data)).catch((err) => { console.error(err); setModels([]); });
    axios.get(`${API}/api/products/bullbar`).then((res) => {
      const live = res.data.find((p) => p.slug === WELLSIDE_TUB_FALLBACK.slug);
      if (live) setWellsideTub(live);
    }).catch((err) => console.error(err));
    trackMetaEvent("ViewContent", {
      content_ids: ["wellside-canopies"],
      content_name: "Wellside Canopies",
      content_type: "product",
      currency: "NZD",
    });
    // Reveals the "Ready to find your canopy?" sticky CTA once the visitor
    // has scrolled roughly past the hero, so it doesn't compete with it —
    // same threshold/pattern as the Bush Cabin page's sticky build CTA.
    const onScroll = () => setShowModelsCTA(window.scrollY > window.innerHeight * 0.85);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Fires once per visit when the "flagship engineering" range banner (X-Series
  // / Bush Cabin split-image proof section) scrolls 30%+ into view — confirms
  // whether visitors are actually reaching/seeing this section before the
  // store below it. Reuses the existing "click"-event analytics pipeline
  // (same one that powers "Top Clicked Buttons"), so it shows up in Admin
  // Panel -> Analytics -> Behaviour Overview without any new reporting UI.
  useEffect(() => {
    if (!rangeBannerRef.current || rangeBannerSeenRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !rangeBannerSeenRef.current) {
            rangeBannerSeenRef.current = true;
            trackEvent("wellside_range_banner_seen");
            observer.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );
    observer.observe(rangeBannerRef.current);
    return () => observer.disconnect();
  }, [models]);

  const storeItems = useMemo(() => (models ? buildStoreItems(models, wellsideTub) : []), [models, wellsideTub]);

  const filteredModels = useMemo(() => {
    if (selectedMake === "all") return storeItems;
    return storeItems.filter((m) => deriveMake(m.name) === selectedMake);
  }, [selectedMake, storeItems]);

  if (models === null) {
    return <div className="min-h-[60vh] flex items-center justify-center text-white/50 font-mono text-sm">Loading…</div>;
  }

  return (
    <div data-testid="wellside-page">
      <WellsideDealerFleetModal open={dealerModalOpen} onOpenChange={setDealerModalOpen} />
      <SEO
        path="/wellside-canopies"
        title="Bushtech Wellside Canopies NZ"
        description="Premium TIG-welded aluminium ute canopies for Hilux, Ranger, Amarok, D-Max, BT-50, Navara, INEOS, Defender and more. 300kg dynamic load. 10-year manufacturer frame guarantee. Genuine OEM-grade construction."
        image={HERO_IMAGES.wellside}
      />

      {/* "Become A Dealer" utility bar — fixed directly below the site's
          main header (which is itself h-20/80px, hence top-20 here), only
          on this page, so it reads as a permanent extension of the header
          rather than a badge competing with the hero's static feature
          badges further down. */}
      <div
        data-testid="wellside-dealer-sticky-bar"
        className="fixed top-20 inset-x-0 z-40 flex items-center justify-center gap-2 md:gap-3 bg-[var(--brand-primary)] text-black py-2 md:py-2.5 px-4 text-center flex-wrap"
      >
        <Handshake size={14} strokeWidth={2} className="flex-shrink-0" />
        <span className="hidden sm:inline font-mono text-[11px] md:text-xs uppercase tracking-[0.15em] font-medium">
          Are you a car dealership, or buying for a fleet?
        </span>
        <button
          type="button"
          onClick={() => setDealerModalOpen(true)}
          data-testid="wellside-become-dealer-badge"
          className="font-mono text-[11px] md:text-xs uppercase tracking-[0.15em] font-semibold underline hover:no-underline"
        >
          Become A Dealer →
        </button>
      </div>

      {/* Sticky "ready to find your canopy?" CTA — mirrors the Bush Cabin
          page's sticky build CTA. Desktop: compact bottom-left box (never
          sits on top of the Chatway bubble at bottom-right). Mobile:
          full-width bottom bar, matching the desktop's appear-after-hero
          behaviour. Links to #models, same destination as the hero's own
          "View Models" button, so no extra Meta tracking added here (it's
          an in-page scroll, not a real navigation). */}
      <div
        data-testid="wellside-sticky-models-cta-desktop"
        className={`hidden md:flex fixed bottom-6 left-6 z-40 items-center gap-4 bg-[var(--bg-main)] border border-[var(--border-default)] border-l-4 border-l-[var(--brand-primary)] shadow-2xl px-6 py-4 transition-all duration-300 ${showModelsCTA ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`}
      >
        <span className="font-display text-lg text-white">Ready to find your canopy?</span>
        <a href="#models" data-testid="wellside-sticky-models-cta-link" className="btn-primary whitespace-nowrap">
          View Models <ArrowUpRight size={14} />
        </a>
      </div>
      <div
        data-testid="wellside-sticky-models-cta-mobile"
        className={`md:hidden fixed bottom-0 inset-x-0 z-40 bg-[var(--bg-main)]/95 backdrop-blur-md border-t border-[var(--border-default)] px-4 flex transition-all duration-300 ${showModelsCTA ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`}
        style={{ paddingTop: "0.75rem", paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
      >
        <a
          href="#models"
          data-testid="wellside-sticky-models-cta-link-mobile"
          className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[var(--brand-primary)] text-black text-xs uppercase tracking-wider py-3 font-semibold"
        >
          <LayoutGrid size={14} /> View Models
        </a>
      </div>

      {/* HERO */}
      <section className="relative overflow-hidden bg-[var(--bg-main)] md:min-h-[100svh] md:flex md:items-end">
        {/* Mobile-only spacer so the fixed header (80px) + dealer bar (~36px)
            never sit on top of the hero photo itself — previously they
            overlaid the top of the image, covering the canopy on the
            vehicle. On mobile the image now starts fresh below both fixed
            bars instead of being covered by them. Desktop is unaffected
            (dealer bar there sits over background, not the focal canopy). */}
        <div className="h-[116px] md:hidden" aria-hidden="true" />
        <div className="relative aspect-[4/5] md:absolute md:inset-0 md:aspect-auto">
          {/* Mobile gets a separately-cropped version of this same photo (top
              ~245px removed — a blurred canvas-extension artifact baked into
              the original file, only noticeable on mobile's top-anchored
              crop). Desktop keeps the original file completely untouched. */}
          <picture>
            <source media="(max-width: 767px)" srcSet={HERO_IMAGES.wellsideMobile} />
            <img src={HERO_IMAGES.wellside} alt="Bushtech wellside canopy" className="w-full h-full object-cover object-[50%_0%]" />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-main)] from-0% via-black/55 via-30% to-transparent to-55% md:from-10% md:via-black/10 md:via-45%" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/15 via-black/0 to-transparent md:from-black/32 md:via-black/4 md:via-transparent" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 from-0% via-black/5 via-10% to-transparent to-18% pointer-events-none" />
        </div>
        <div className="relative z-10 -mt-[50%] md:mt-0 w-full">
          <div className="container-x pt-4 pb-4 md:pb-20 md:pt-32">
          <h1 className="font-display text-[9vw] md:text-[6vw] lg:text-[5vw] leading-[0.95] text-white hero-text-shadow max-w-[26ch]">
            SOUTH AFRICAN <span className="text-[var(--brand-primary)]">BUILT.</span><br />
            FULLY WELDED.<br />
            NOT BOLT-TOGETHER.
          </h1>
          <p className="mt-4 md:mt-6 text-white/85 text-base md:text-lg leading-relaxed max-w-xl hero-text-shadow">
            A fully TIG-welded aluminium frame, class-leading dust sealing and a 10-year manufacturer frame guarantee. Premium construction at a price that may surprise you.
          </p>
          <div className="mt-4 md:mt-8">
            <a href="#models" className="btn-primary" data-testid="wellside-cta-models">View Models <ArrowUpRight size={14} /></a>
          </div>
          {/* Hero Trust Badge Stack — same 3 green-tick badges shown on every
              Wellside Canopy product page, now also on the category hero. */}
          <HeroTrustBadgesV2 />
          </div>
        </div>
      </section>

      {/* CONSTRUCTION DIFFERENTIATOR — sits directly below the hero, before
          the model grid, since the store was moved up and most visitors
          never scroll far enough to reach the full "what sets Bushtech
          apart" section further down. Replaces the old scrolling stats
          marquee here, which repeated the same 3 facts (SA made, TIG-welded,
          10-year guarantee) in a weaker format right after this. */}
      <section className="container-x pt-12 md:pt-16 pb-10 md:pb-14 border-t border-b border-[#C2B280]/30" data-testid="wellside-construction-differentiator">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 lg:items-center">
          <div className="lg:col-span-4">
            <div className="border border-[#C2B280]/50 px-6 py-8 md:px-8 md:py-10 text-center lg:text-left">
              <div className="flex items-baseline justify-center lg:justify-start gap-3">
                <p className="font-display text-6xl md:text-7xl text-[var(--brand-primary)] leading-none">10</p>
                <p className="font-display text-6xl md:text-7xl text-[var(--brand-primary)] leading-none">YEAR</p>
              </div>
              <p className="font-mono text-sm md:text-base tracking-[0.15em] uppercase text-[var(--text-secondary)] mt-4">
                Manufacturer<br />Frame Guarantee
              </p>
            </div>
          </div>
          <div className="lg:col-span-8">
            <p className="overline mb-4">— South African Made. Built To Last.</p>
            <h2 className="font-display text-4xl md:text-5xl text-white leading-[0.95]">
              CANOPIES CAN LOOK SIMILAR.<br />
              <span className="text-[var(--brand-primary)]">A BUSHTECH IS BUILT DIFFERENTLY.</span>
            </h2>
            <p className="mt-5 text-[var(--text-secondary)] text-lg leading-relaxed max-w-2xl">
              With low-cost flat-pack canopies flooding the market, it's worth looking beneath the finish.
            </p>
            <p className="mt-4 text-[var(--text-secondary)] text-lg leading-relaxed max-w-2xl">
              <strong className="text-white font-semibold">Made in South Africa</strong>, Bushtech canopies feature a <strong className="text-white font-semibold">fully TIG-welded aluminium frame</strong> and arrive fully assembled — <strong className="text-white font-semibold">not as a bolt-together kit</strong>.
            </p>
            <p className="mt-4 text-[var(--text-secondary)] text-lg leading-relaxed max-w-2xl">
              That construction is backed by a 10-year manufacturer's frame guarantee — well beyond the 12–24 month structural warranties on bolt-together canopies.
            </p>
            <a href="#evidence-heading" className="inline-flex items-center gap-2 mt-5 font-mono text-sm tracking-[0.15em] uppercase text-[var(--brand-primary)] hover:text-white transition-colors border-b border-[var(--brand-primary)] hover:border-white pb-0.5" data-testid="wellside-construction-link">
              See What Sets Bushtech Apart <ChevronDown size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* SAME QUALITY ACROSS THE RANGE — proof-by-association banner. Pairs
          the flagship X-Series and Bush Cabin builds (which visibly read as
          serious, premium builds) to show Wellside canopies share the same
          construction standard. Sits right before the store grid so the
          price that follows reads as accessible, not bottom-of-market.
          Images use their native 4:3 ratio so nothing is cropped out. The
          headline sits overlaid in the sky/background band at the top of
          each photo (above the truck/canopy itself), not over the vehicles,
          keeping scroll minimal without blocking the product shots. */}
      <section ref={rangeBannerRef} className="overflow-hidden" data-testid="wellside-range-banner">
        <div className="relative">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--brand-primary)]/40">
          <div className="relative aspect-[4/3] bg-black">
            <img
              src="/products/xseries/range-xseries-mountain.webp"
              alt="Bushtech X-Series tray canopy in the Southern Alps"
              loading="lazy"
              className="w-full h-full object-cover object-center"
            />
            <p className="absolute bottom-3 left-1/2 -translate-x-1/2 md:bottom-[95px] font-mono text-[10px] md:text-sm font-black tracking-[0.15em] uppercase text-white [text-shadow:0_1px_5px_rgba(0,0,0,0.95)]">X-Series</p>
          </div>
          <div className="relative aspect-[4/3] bg-black">
            <img
              src="/products/wellsides/range-bushcabin-beach.webp"
              alt="Bushtech Bush Cabin expedition camper at the beach"
              loading="lazy"
              className="w-full h-full object-cover object-center"
            />
            <p className="absolute top-3 left-1/2 -translate-x-1/2 md:top-auto md:bottom-[95px] font-mono text-[10px] md:text-sm font-black tracking-[0.15em] uppercase text-white [text-shadow:0_1px_5px_rgba(0,0,0,0.95)]">Bush Cabin</p>
          </div>
        </div>
        {/* Headline sits in the top sky/background band (above the trucks).
            Mobile band height increased to comfortably fit the bigger
            mobile headline below without losing contrast against the sky. */}
        <div className="absolute inset-x-0 top-0 h-[130px] md:h-[230px] bg-gradient-to-b from-black/90 via-black/55 to-transparent pointer-events-none" />
        <div className="absolute inset-x-0 top-0 pt-3 md:pt-7 px-3 md:px-4 text-center">
          {/* Mobile-only size bumped to sit closer to the hero heading size
              (hero uses text-[9vw] on mobile) — was too small relative to
              the hero at text-xl. sm/md unchanged (already correct there). */}
          <h2 className="font-display text-[9vw] sm:text-3xl md:text-5xl text-white leading-[1.05] [text-shadow:0_2px_10px_rgba(0,0,0,0.9)]">
            YOU'RE HERE FOR A CANOPY.<br />
            <span className="text-[var(--brand-primary)]">YOU'RE GETTING FLAGSHIP ENGINEERING.</span>
          </h2>
        </div>
        {/* Bottom fade — transitions the image seamlessly into the solid
            black copy band beneath, so the bigger standout text below
            reads as a continuation of the image rather than a hard cut. */}
        <div className="absolute inset-x-0 bottom-0 h-[70px] md:h-[130px] bg-gradient-to-t from-black via-black/70 to-transparent pointer-events-none" />
      </div>
      {/* Standout copy band — sits in normal flow directly beneath the
          images (not an overlay), so the bigger, bolder message never has
          to compete with or cover the trucks. The fade above blends
          straight into this band's solid black, keeping it visually
          joined to the photos above it. */}
      <div className="relative bg-black pt-1 pb-7 md:pb-10 px-2 sm:px-4 md:px-6 text-center">
        {/* Mobile-only: bigger + no forced break, so it wraps naturally
            (now 3 lines instead of the previous forced-2-line 12px version)
            per explicit request to prioritise size over exact line count.
            sm/md/lg keep the tuned exactly-2-line version unchanged. */}
        <p className="font-display tracking-tight text-lg sm:text-base md:text-xl lg:text-3xl text-white leading-snug md:leading-tight max-w-2xl sm:max-w-3xl md:max-w-4xl lg:max-w-5xl mx-auto" data-testid="wellside-range-demo-note">
          <span className="sm:hidden">Our Wellside canopies share the materials, engineering and finish behind our flagship tray canopies and Bush Cabin campers.</span>
          <span className="hidden sm:inline">
            Our Wellside canopies share the materials, engineering<br />
            and finish behind our flagship tray canopies and Bush Cabin campers.
          </span>
        </p>
        <p className="mt-3 md:mt-4 font-mono text-sm md:text-lg text-[var(--brand-primary)] tracking-wide max-w-2xl mx-auto">
          Premium build quality, at a more accessible price than many NZ-made canopies.
        </p>
      </div>
      </section>

      {/* MODELS GRID — moved directly below the hero (was previously after
          6 long trust/proof sections, ~55-58% down the page). Analytics
          showed avg scroll depth on this page is only ~53%, and 91% of
          visitors arrive here directly already looking for their specific
          ute — most were never reaching the vehicle picker at all. */}
      <section id="models" className="container-x pt-4 md:pt-8 pb-12">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-6">
          <div>
            <p className="overline mb-4">— Shop By Vehicle</p>
            <h2 className="font-display text-4xl md:text-6xl text-white leading-[0.95]">
              FIT YOUR UTE.
            </h2>
          </div>
        </div>

        {/* Trust strip removed — redundant with the differentiator section
            and range banner directly above, and the Engineering section
            below (300kg load, NZ dealership fitment, 2.5t tested all repeat
            there). Was also crowding on mobile. */}

        {/* Vehicle filter chips */}
        <div className="flex flex-wrap gap-2 mb-10" data-testid="wellside-vehicle-filter">
          <button
            onClick={() => setSelectedMake("all")}
            data-testid="wellside-vehicle-filter-all"
            className={`font-mono text-[11px] tracking-[0.2em] uppercase px-4 py-2 border transition-colors ${
              selectedMake === "all"
                ? "bg-[var(--brand-primary)] text-black border-[var(--brand-primary)]"
                : "text-white border-[var(--border-default)] hover:border-[var(--brand-primary)]"
            }`}
          >
            All Vehicles ({storeItems.length})
          </button>
          {MAKE_ORDER.map((make) => {
            const count = storeItems.filter((m) => deriveMake(m.name) === make).length;
            if (count === 0) return null;
            return (
              <button
                key={make}
                onClick={() => setSelectedMake(make)}
                data-testid={`wellside-vehicle-filter-${make.toLowerCase().replace(/\s+/g, "-")}`}
                className={`font-mono text-[11px] tracking-[0.2em] uppercase px-4 py-2 border transition-colors ${
                  selectedMake === make
                    ? "bg-[var(--brand-primary)] text-black border-[var(--brand-primary)]"
                    : "text-white border-[var(--border-default)] hover:border-[var(--brand-primary)]"
                }`}
              >
                {make} ({count})
              </button>
            );
          })}
        </div>

        {filteredModels.length === 0 ? (
          <p className="text-[var(--text-secondary)] py-10 text-center font-mono text-xs uppercase tracking-[0.2em]">
            No canopies match. Try a different vehicle filter.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredModels.map((m, i) => <WellsideCard key={m.slug} m={m} index={i} />)}
          </div>
        )}
      </section>

      {/* THE STORY */}
      <section id="story-section" className="container-x pt-4 md:pt-12 pb-0">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 lg:items-center">
          <div className="lg:col-span-5">
            <p className="overline mb-6">— The Engineering</p>
            <h2 className="font-display text-4xl md:text-5xl leading-[0.95] text-white">
              50% STRONGER THAN THE<br />
              <span className="text-[var(--brand-primary)]">NEAREST COMPETITOR.</span>
            </h2>
            <p className="mt-3 text-white/80 text-lg leading-relaxed max-w-md">
              300kg dynamic load rating (tested to 2.5 tonnes) — yet lighter than even plastic & fibreglass canopies.
            </p>
          </div>
          <div className="lg:col-span-7 space-y-3 text-[var(--text-secondary)] text-lg leading-relaxed">
            <div className="md:hidden aspect-[4/3] overflow-hidden border border-[var(--border-default)]" data-testid="story-mobile-image">
              <img
                src="/products/wellsides/story_mobile_hilux_doors_open.webp"
                alt="2026 Toyota Hilux with Bushtech canopy, both gullwing doors open, showing roof rack fitment"
                loading="lazy"
                className="w-full h-full object-cover object-center"
              />
            </div>
            <p className="text-white">
              Developed in South Africa for extreme conditions, proven internationally, and now trusted for fitment at NZ new-vehicle dealerships.
            </p>
          </div>
        </div>
      </section>

      {/* PHOTOGRAPHIC EVIDENCE */}
      <section className="container-x pt-10 md:pt-12 pb-24 md:pb-32">
        <div id="evidence-heading" className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-10 scroll-mt-36">
          <div className="lg:col-span-5" />
          <div className="lg:col-span-7">
            <h2 className="font-display text-4xl md:text-5xl text-white leading-[0.95] whitespace-nowrap">
              SEE WHAT SETS <br className="md:hidden" /><span className="text-[var(--brand-primary)]">BUSHTECH APART.</span>
            </h2>
          </div>
        </div>

        <div className="space-y-16 md:space-y-24">
          {PROOF_POINTS.map((p, i) => (
            <div
              key={p.title}
              id={i === 1 ? "seal-integrity-point" : undefined}
              data-testid={`evidence-point-${i}`}
              className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}
            >
              <div className="lg:col-span-5">
                <div className="relative aspect-[4/3] overflow-hidden border border-[var(--border-default)] bg-[var(--bg-tertiary)]">
                  <img
                    src={p.image}
                    alt={p.title}
                    loading="lazy"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: p.objectPosition || "center", ...(p.imageStyle || {}) }}
                  />
                  {i === 1 && (
                    <svg
                      className="absolute inset-0 w-full h-full pointer-events-none"
                      viewBox="0 0 400 300"
                      preserveAspectRatio="none"
                      data-testid="seal-integrity-arrow"
                    >
                      <defs>
                        <marker id="seal-arrowhead" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto">
                          <path d="M0,0 L8,4 L0,8 Z" fill="var(--brand-primary)" />
                        </marker>
                      </defs>
                      <text x="14" y="58" fill="var(--brand-primary)" fontSize="15" fontFamily="var(--font-mono, monospace)" fontWeight="700" letterSpacing="0.5">
                        SUSPENSION RUBBER BASE
                      </text>
                      <path d="M108 70 Q90 130 100 205" fill="none" stroke="var(--brand-primary)" strokeWidth="2.5" markerEnd="url(#seal-arrowhead)" />
                    </svg>
                  )}
                </div>
              </div>
              <div className="lg:col-span-7">
                <p className="font-mono text-xs tracking-[0.3em] uppercase text-[var(--brand-primary)] mb-4">{p.label}</p>
                <h3 className="font-display text-3xl md:text-4xl text-white leading-tight">{p.title}</h3>
                {p.lead && (
                  <p className="text-white font-semibold mt-6 leading-relaxed text-lg">{p.lead}</p>
                )}
                <p className={`text-[var(--text-secondary)] leading-relaxed text-lg ${p.lead ? "mt-3" : "mt-6"} ${expandedProof[i] ? "block" : "hidden md:block"}`}>
                  {p.body}
                  {p.emphasis && <span className="text-white font-semibold"> {p.emphasis}</span>}
                </p>
                <button
                  type="button"
                  onClick={() => setExpandedProof((prev) => ({ ...prev, [i]: !prev[i] }))}
                  className="md:hidden mt-4 inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-[var(--brand-primary)] hover:text-white transition-colors"
                  data-testid={`evidence-point-toggle-${i}`}
                >
                  {expandedProof[i] ? "Read Less" : "Read More"}
                  <ChevronDown size={14} className={`transition-transform ${expandedProof[i] ? "rotate-180" : ""}`} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CUSTOMER TESTIMONIALS — closing trust point right before the
          enquiry banner, same Quote-card treatment used on the Ground
          Tents page. Only initials used for attribution, not the full
          names these customers posted publicly under. */}
      <section className="container-x pb-16 md:pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl mx-auto">
          {TESTIMONIALS.map((t) => (
            <div key={t.attribution} data-testid={`wellside-testimonial-${t.attribution.toLowerCase().replace(/[^a-z]/g, "")}`} className="border border-[var(--border-default)] bg-[var(--bg-secondary)] p-7 md:p-8">
              <Quote size={28} className="text-[var(--brand-primary)]" strokeWidth={1.6} />
              <p className="text-lg text-white leading-relaxed mt-4">
                "{t.quote}"
              </p>
              <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-[var(--brand-primary)] mt-5">
                — {t.attribution} · Bushtech Wellside Canopy Customer
              </p>
            </div>
          ))}
        </div>
      </section>

      <DemoBanner product="Bushtech Wellside Canopy" enquiryProduct="Wellside Canopy" />
    </div>
  );
}
